import { FC, PropsWithChildren, ReactNode, useMemo } from 'react';
import { Route, Switch, useInitializeRoute } from './ReactRouteUtils';
import { Ng2React } from '../../common/util/Ng2React';
import dashboardPage from '../../legacy/app/dashboard/dashboardPage';
import dashboardIndexPage from '../../legacy/app/dashboardIndex/dashboardIndexPage';
import { useUserCapabilitiesUtils } from '../../common/ProtectedComponentUtils';
import { ProtectedComponent } from '@splunk/olly-common/RBAC';
import type { Endpoint } from '@splunk/olly-common/RBAC';
import { Capability } from '@splunk/olly-services/lib/services/CurrentUser/Capabilities';
import { dashboardsFeaturesEndpointMap, teamEndpoints } from './dashboardsFeaturesEndpointMap';
import type { EndpointsType } from './dashboardsFeaturesEndpointMap';
import { ForbiddenComponent } from '@splunk/olly-common/ForbiddenComponent';
import snapshotDashboardPage from '../../legacy/app/dashboard/snapshotDashboardPage';

interface DashboardProtectedWrapperProps {
    pageType: EndpointsType;
    fallbackUi: ReactNode;
}

export const DashboardProtectedWrapper: FC<PropsWithChildren<DashboardProtectedWrapperProps>> = ({
    pageType,
    fallbackUi,
    children,
}) => {
    const { capabilities } = useUserCapabilitiesUtils();

    const endpoints: Endpoint[] = useMemo(() => {
        const baseEndpoints = dashboardsFeaturesEndpointMap[pageType];
        if (!capabilities.includes(Capability.READ_TEAM)) {
            return baseEndpoints;
        }
        return [...baseEndpoints, ...teamEndpoints[pageType]];
    }, [pageType, capabilities]);

    return (
        <ProtectedComponent
            userCapabilities={capabilities}
            endpoints={endpoints}
            fallbackUi={fallbackUi}
        >
            {children}
        </ProtectedComponent>
    );
};

export const DashboardRoutes: FC = () => {
    const routeUtilsBootStrap = useInitializeRoute();

    return (
        <Switch>
            <Route
                exact
                path={['/dashboards', '/dashboards/:orgID']}
                title="Dashboards"
                straps={[routeUtilsBootStrap]}
            >
                <DashboardProtectedWrapper
                    pageType="viewDashboardIndexPage"
                    fallbackUi={<ForbiddenComponent />}
                >
                    <Ng2React name="dashboardIndexPage" definition={dashboardIndexPage} />
                </DashboardProtectedWrapper>
            </Route>

            <Route
                exact
                path={['/page/:groupId', '/dashboard/:dashboardID']}
                title="Dashboard"
                straps={[routeUtilsBootStrap]}
                reloadWithParams="path"
            >
                <DashboardProtectedWrapper
                    pageType="viewDashboardPage"
                    fallbackUi={<ForbiddenComponent />}
                >
                    <Ng2React name="dashboardPage" definition={dashboardPage} />
                </DashboardProtectedWrapper>
            </Route>

            <Route
                exact
                path="/temp/dashboard/:snapshotID"
                title="Dashboard"
                straps={[routeUtilsBootStrap]}
                reloadWithParams="path"
            >
                <DashboardProtectedWrapper
                    pageType="viewDashboardPage"
                    fallbackUi={<ForbiddenComponent />}
                >
                    <Ng2React name="snapshotDashboardPage" definition={snapshotDashboardPage} />
                </DashboardProtectedWrapper>
            </Route>

            {/* Export route for non-interactive dashboard rendering */}
            <Route
                exact
                path={['/_/dashboard/export/:dashboardID']}
                title="Dashboard Export"
                straps={[routeUtilsBootStrap]}
                reloadWithParams="path"
            >
                <DashboardProtectedWrapper
                    pageType="viewDashboardPage"
                    fallbackUi={<ForbiddenComponent />}
                >
                    <Ng2React name="dashboardPage" definition={dashboardPage} captureMode={true} />
                </DashboardProtectedWrapper>
            </Route>
        </Switch>
    );
};
